(() => {
  const PROVINCES = [{"name":"Eastern Cape","slug":"eastern-cape"},{"name":"Free State","slug":"free-state"},{"name":"Gauteng","slug":"gauteng"},{"name":"KwaZulu-Natal","slug":"kwazulu-natal"},{"name":"Limpopo","slug":"limpopo"},{"name":"Mpumalanga","slug":"mpumalanga"},{"name":"North West","slug":"north-west"},{"name":"Northern Cape","slug":"northern-cape"},{"name":"Western Cape","slug":"western-cape"}];
  const SOUTH_AFRICA = "All South Africa";
  const SPAR_STORE_CATEGORY = "spar";
  const SPAR_CHAIN_REGEX = /\b(?:kwik[\s-]*spar|super[\s-]*spars?|spar)\b/i;
  const dataCache = new Map();
  const mapsCache = {};

  const escapeHtml = (value) => String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

  const clean = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
  const slugify = (value) => clean(value).toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  const normalizePhone = (value) => clean(value).replace(/[^+\d]/g, "");
  const hasPhone = (value) => normalizePhone(value).length >= 7;
  const mapsUrl = (record) => {
    if (Number.isFinite(record.lat) && Number.isFinite(record.lng)) {
      return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(record.lat + "," + record.lng);
    }
    return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent([record.name, record.displayLocation, record.country].filter(Boolean).join(", "));
  };
  const isSparStore = (record) => record?.type === "retailer" && (record?.category === SPAR_STORE_CATEGORY || SPAR_CHAIN_REGEX.test([record.name, record.address, record.primaryTown, record.displayLocation, record.formattedAddress, record.locality].filter(Boolean).join(" ")));
  const typeLabel = (record) => isSparStore(record) ? "SPAR Store" : "Retailer";
  const placeLabel = (record) => clean(record.primaryTown || record.locality || record.displayLocation || record.formattedAddress || record.province);
  const coverageLabel = (record) => clean(record.address || record.formattedAddress || record.coverage || record.displayLocation);

  function provinceFromHandle(handle) {
    const normalized = clean(handle).replace(/^stockists-/, "");
    const match = PROVINCES.find((province) => province.slug === normalized);
    return match?.name || "";
  }

  function byName(a, b) {
    return clean(a.name).localeCompare(clean(b.name));
  }

  function distanceKm(origin, destination) {
    const radius = 6371;
    const lat1 = origin.lat * Math.PI / 180;
    const lat2 = destination.lat * Math.PI / 180;
    const dLat = (destination.lat - origin.lat) * Math.PI / 180;
    const dLng = (destination.lng - origin.lng) * Math.PI / 180;
    const calc = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return radius * 2 * Math.atan2(Math.sqrt(calc), Math.sqrt(1 - calc));
  }

  async function loadData(url) {
    if (!dataCache.has(url)) {
      dataCache.set(url, fetch(url, { headers: { Accept: "application/json" } }).then((response) => {
        if (!response.ok) throw new Error("Stockist data failed to load.");
        return response.json();
      }));
    }
    return dataCache.get(url);
  }

  function loadMaps(key) {
    if (window.google?.maps?.Map) return Promise.resolve(window.google);
    if (!key) return Promise.reject(new Error("Google Maps key is missing."));
    if (!mapsCache.promise) {
      mapsCache.promise = new Promise((resolve, reject) => {
        const existing = document.querySelector("script[data-fl-google-maps]");
        if (existing) {
          if (window.google?.maps?.Map) resolve(window.google);
          existing.addEventListener("load", () => window.google?.maps?.Map ? resolve(window.google) : reject(new Error("Google Maps loaded without Map.")), { once: true });
          existing.addEventListener("error", () => reject(new Error("Google Maps failed to load.")), { once: true });
          return;
        }
        const callbackName = "__flStockistsGoogleMapsReady";
        window[callbackName] = () => resolve(window.google);
        const script = document.createElement("script");
        script.async = true;
        script.defer = true;
        script.dataset.flGoogleMaps = "true";
        script.src = "https://maps.googleapis.com/maps/api/js?loading=async&callback=" + encodeURIComponent(callbackName) + "&key=" + encodeURIComponent(key);
        script.addEventListener("error", () => reject(new Error("Google Maps failed to load.")), { once: true });
        document.head.appendChild(script);
      });
    }
    return mapsCache.promise;
  }

  function provinceMeta(data, provinceName) {
    return (data.provinces || []).find((entry) => entry.label === provinceName || entry.key === provinceName) || null;
  }

  function recordsForProvince(data, provinceName) {
    return (data.records || [])
      .filter((record) => record.type === "retailer" && record.country === "South Africa" && record.province === provinceName)
      .sort(byName);
  }

  function recordMatches(record, query, type) {
    if (type === "spar" && !isSparStore(record)) return false;
    if (type === "retailer" && isSparStore(record)) return false;
    if (record.type !== "retailer") return false;
    if (!query) return true;
    const haystack = [record.name, record.type, record.province, record.country, record.address, record.formattedAddress, record.primaryTown, record.locality, record.coverage, record.phone, record.email, record.displayLocation, record.category]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(query.toLowerCase());
  }

  function storeCard(record, options = {}) {
    const selected = options.selectedId === record.id ? " is-selected" : "";
    const phone = hasPhone(record.phone) ? '<a href="tel:' + escapeHtml(normalizePhone(record.phone)) + '">' + escapeHtml(record.phone) + '</a>' : "";
    const email = record.email ? '<a href="mailto:' + escapeHtml(record.email) + '">' + escapeHtml(record.email) + '</a>' : "";
    const contact = [phone, email].filter(Boolean).join(" ");
    return '<article class="fl-stockists__store-card' + selected + '" data-record-id="' + escapeHtml(record.id) + '">' +
      '<div class="fl-stockists__store-top"><div><h3>' + escapeHtml(record.name || "Stockist") + '</h3><p class="fl-stockists__store-meta">' + escapeHtml(placeLabel(record)) + '</p></div><span class="fl-stockists__badge">' + escapeHtml(typeLabel(record)) + '</span></div>' +
      '<p>' + escapeHtml(coverageLabel(record) || "Contact this stockist for availability.") + '</p>' +
      (contact ? '<p class="fl-stockists__store-meta">' + contact + '</p>' : '<p class="fl-stockists__store-meta">Contact Flippen Lekka for current details.</p>') +
      '<div class="fl-stockists__card-actions"><a href="' + escapeHtml(mapsUrl(record)) + '" target="_blank" rel="noopener">Open in Google Maps</a>' +
      (options.showProvince ? '<a href="/pages/stockists-' + escapeHtml(slugify(record.province)) + '">View province</a>' : '') +
      '</div>' +
    '</article>';
  }

  function renderHub(root, data) {
    const all = (data.records || []).filter((record) => record.type === "retailer" && record.country === "South Africa");
    const allMeta = (data.provinces || []).find((entry) => entry.key === SOUTH_AFRICA) || { retailerCount: 0, sparStoreCount: 0 };
    const mappedCount = all.filter((record) => Number.isFinite(record.lat) && Number.isFinite(record.lng)).length;
    const productUrl = escapeHtml(root.dataset.productUrl || "");
    const productAltUrl = escapeHtml(root.dataset.productAltUrl || "");
    root.innerHTML = '<div class="fl-stockists__wrap">' +
      '<section class="fl-stockists__hero"><div class="fl-stockists__hero-copy"><div><p class="fl-stockists__eyebrow">Find us on shelves</p><h1>Flippen Lekka near you.</h1><p class="fl-stockists__intro">Search verified retailer locations across South Africa by province, town or store name. Product availability can vary, so contact the retailer before travelling for a specific blend or pack size.</p><a class="fl-stockists__button fl-stockists__button--hero" href="#find-a-stockist">Find a stockist</a></div></div>' +
      '<div class="fl-stockists__hero-visual"><p>From local favourites to national shelves.</p><div class="fl-stockists__hero-products">' +
      (productUrl ? '<img src="' + productUrl + '" alt="Flippen Lekka Original multi-purpose spice" width="420" height="420">' : '') +
      (productAltUrl ? '<img src="' + productAltUrl + '" alt="Flippen Lekka Hot and Spicy multi-purpose spice" width="420" height="420">' : '') +
      '</div><span>' + escapeHtml(allMeta.retailerCount || all.length) + ' South African retailer locations</span></div></section>' +
      '<section class="fl-stockists__proof"><dl class="page-width"><div><dt>' + escapeHtml(allMeta.retailerCount || all.length) + '</dt><dd>Retailer locations</dd></div><div><dt>' + escapeHtml(allMeta.sparStoreCount || 0) + '</dt><dd>SPAR locations</dd></div><div><dt>9</dt><dd>Provinces</dd></div><div><dt>' + escapeHtml(mappedCount) + '</dt><dd>Mapped retailers</dd></div></dl></section>' +
      '<section id="find-a-stockist" class="fl-stockists__search-panel page-width"><div class="fl-stockists__section-heading"><p>Search the directory</p><h2>Find the closest retailer.</h2></div><div class="fl-stockists__toolbar"><div class="fl-stockists__search"><label for="flStockistHubSearch">Store, town, province or phone</label><input id="flStockistHubSearch" type="search" placeholder="Start typing a location or retailer name"></div><div class="fl-stockists__actions"><button class="fl-stockists__button" type="button" data-nearest>Use my location</button></div></div><div data-hub-results class="fl-stockists__cards fl-stockists__search-results"></div></section>' +
      '<section class="fl-stockists__provinces page-width"><div class="fl-stockists__section-heading"><p>Browse by province</p><h2>South African stockists.</h2><span>Choose a province to open its retailer directory and map.</span></div><div class="fl-stockists__province-grid">' +
      PROVINCES.map((province) => {
        const meta = provinceMeta(data, province.name) || { retailerCount: 0 };
        return '<a class="fl-stockists__province-card" href="/pages/stockists-' + escapeHtml(province.slug) + '"><span class="fl-stockists__province-index">Province</span><span class="fl-stockists__province-name">' + escapeHtml(province.name) + '</span><span class="fl-stockists__province-meta"><strong>' + escapeHtml(meta.retailerCount || 0) + '</strong> retailer locations</span><span class="fl-stockists__province-link">View directory →</span></a>';
      }).join("") +
      '</div></section><section class="fl-stockists__routes"><div><p class="fl-stockists__eyebrow">For retailers</p><h2>Want to stock Flippen Lekka?</h2><p>Butcheries, farm stalls, delis, restaurants and independent grocers can enquire about retail-ready bottles, case quantities and selected bulk formats.</p><a href="/pages/wholesale">Wholesale enquiries →</a></div><div><p class="fl-stockists__eyebrow">Outside South Africa?</p><h2>International availability.</h2><p>Importers, distributors and international retailers can send a focused market enquiry to the team.</p><a href="/pages/international-export">International &amp; export →</a></div></section></div>';

    const input = root.querySelector("#flStockistHubSearch");
    const results = root.querySelector("[data-hub-results]");
    const renderResults = (records, label) => {
      results.innerHTML = records.length
        ? '<p class="fl-stockists__small">' + escapeHtml(label) + '</p>' + records.slice(0, 12).map((record) => storeCard(record, { showProvince: true })).join("")
        : '<div class="fl-stockists__empty">No matching stockists found. Try a nearby town or province.</div>';
    };
    input.addEventListener("input", () => {
      const query = clean(input.value);
      if (!query) {
        results.innerHTML = "";
        return;
      }
      renderResults(all.filter((record) => recordMatches(record, query, "all")).sort(byName), "Showing the first matching stockists.");
    });
    root.querySelector("[data-nearest]").addEventListener("click", () => {
      if (!navigator.geolocation) {
        renderResults([], "Location is not available in this browser.");
        return;
      }
      results.innerHTML = '<div class="fl-stockists__empty">Checking your location...</div>';
      navigator.geolocation.getCurrentPosition((position) => {
        const origin = { lat: position.coords.latitude, lng: position.coords.longitude };
        const nearest = all
          .filter((record) => Number.isFinite(record.lat) && Number.isFinite(record.lng))
          .map((record) => ({ ...record, distance: distanceKm(origin, record) }))
          .sort((a, b) => a.distance - b.distance)
          .slice(0, 12);
        renderResults(nearest, "Nearest stockists based on your current location.");
      }, () => {
        results.innerHTML = '<div class="fl-stockists__empty">Location permission was not granted. Search by town or province instead.</div>';
      });
    });
  }

  function renderProvince(root, data, provinceName, googleKey) {
    const meta = provinceMeta(data, provinceName) || { retailerCount: 0, sparStoreCount: 0 };
    const provinceRecords = recordsForProvince(data, provinceName);
    const mappedCount = provinceRecords.filter((record) => Number.isFinite(record.lat) && Number.isFinite(record.lng)).length;
    const PAGE_SIZE = 24;
    const state = { type: "all", query: "", selectedId: "", visible: PAGE_SIZE, map: null, markers: new Map(), info: null };
    root.innerHTML = '<div class="fl-stockists__wrap">' +
      '<section class="fl-stockists__province-hero"><div class="page-width"><p class="fl-stockists__eyebrow">Flippen Lekka stockists</p><h1>' + escapeHtml(provinceName) + '</h1><p>Browse verified retailer locations, search the directory and open directions before you travel.</p><a href="/pages/store-locator">← All provinces</a></div></section>' +
      '<section class="fl-stockists__proof"><dl class="page-width"><div><dt>' + escapeHtml(meta.retailerCount || provinceRecords.length) + '</dt><dd>Retailer locations</dd></div><div><dt>' + escapeHtml(meta.sparStoreCount || 0) + '</dt><dd>SPAR locations</dd></div><div><dt>' + escapeHtml(mappedCount) + '</dt><dd>Mapped retailers</dd></div></dl></section>' +
      '<section class="fl-stockists__province-tools page-width"><div class="fl-stockists__toolbar"><div class="fl-stockists__search"><label for="flProvinceSearch">Search ' + escapeHtml(provinceName) + '</label><input id="flProvinceSearch" type="search" placeholder="Store, town, suburb or phone"></div><div class="fl-stockists__chips"><button class="fl-stockists__chip is-active" type="button" data-type="all">All retailers</button><button class="fl-stockists__chip" type="button" data-type="retailer">Independent stores</button><button class="fl-stockists__chip" type="button" data-type="spar">SPAR stores</button></div></div><p class="fl-stockists__small">Product availability can vary by retailer. Contact the location before travelling for a specific blend or pack size.</p></section>' +
      '<div class="fl-stockists__view-switcher page-width" data-view-switcher role="group" aria-label="Directory view"><button class="is-active" type="button" data-view="list" aria-pressed="true">List</button><button type="button" data-view="map" aria-pressed="false">Map</button></div>' +
      '<section class="fl-stockists__layout page-width" data-province-layout data-mobile-view="list"><div class="fl-stockists__map-panel"><div class="fl-stockists__map-head"><div><p class="fl-stockists__eyebrow">Map</p><h2>Locations</h2></div><div class="fl-stockists__legend"><span><i class="fl-stockists__dot"></i>Retailers</span><span><i class="fl-stockists__dot fl-stockists__dot--spar"></i>SPAR stores</span></div></div><div class="fl-stockists__map" data-map></div></div><div class="fl-stockists__directory"><div class="fl-stockists__directory-head"><h2>Directory</h2><strong data-count aria-live="polite"></strong></div><div class="fl-stockists__cards" data-cards></div><button class="fl-stockists__load-more" type="button" data-load-more hidden>Show more locations</button></div></section>' +
      '<section class="fl-stockists__routes"><div><p class="fl-stockists__eyebrow">Trade enquiry</p><h2>Want to stock Flippen Lekka?</h2><p>Apply for the wholesale route that best fits your retail business.</p><a href="/pages/wholesale">Wholesale enquiries →</a></div><div><p class="fl-stockists__eyebrow">Need help?</p><h2>Contact the team.</h2><p>Send the store name, town and product you are looking for.</p><a href="/pages/contact">Contact Flippen Lekka →</a></div></section></div>';

    const input = root.querySelector("#flProvinceSearch");
    const cards = root.querySelector("[data-cards]");
    const count = root.querySelector("[data-count]");
    const mapEl = root.querySelector("[data-map]");
    const layout = root.querySelector("[data-province-layout]");
    const loadMore = root.querySelector("[data-load-more]");

    const filtered = () => provinceRecords.filter((record) => recordMatches(record, state.query, state.type));

    function markerIcon(record) {
      const category = isSparStore(record) ? "spar" : "store";
      const color = category === "spar" ? "#048346" : "#FEC30D";
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: category === "spar" ? 7.5 : 7,
        fillColor: color,
        fillOpacity: 0.95,
        strokeColor: "#221E1F",
        strokeWeight: 1.5
      };
    }

    function updateMarkers(records) {
      if (!state.map || !window.google?.maps) return;
      for (const marker of state.markers.values()) marker.setMap(null);
      state.markers.clear();
      const bounds = new window.google.maps.LatLngBounds();
      records.forEach((record) => {
        if (!Number.isFinite(record.lat) || !Number.isFinite(record.lng)) return;
        const marker = new window.google.maps.Marker({
          position: { lat: record.lat, lng: record.lng },
          map: state.map,
          title: record.name,
          icon: markerIcon(record)
        });
        marker.addListener("click", () => {
          state.selectedId = record.id;
          const selectedIndex = filtered().findIndex((entry) => entry.id === record.id);
          if (selectedIndex >= state.visible) state.visible = selectedIndex + 1;
          state.info.setContent('<strong>' + escapeHtml(record.name) + '</strong><br>' + escapeHtml(typeLabel(record)) + '<br><a href="' + escapeHtml(mapsUrl(record)) + '" target="_blank" rel="noopener">Open in Google Maps</a>');
          state.info.open({ map: state.map, anchor: marker });
          renderCards();
        });
        state.markers.set(record.id, marker);
        bounds.extend(marker.getPosition());
      });
      if (!bounds.isEmpty()) state.map.fitBounds(bounds, 64);
    }

    function renderCards() {
      const records = filtered();
      const visibleRecords = records.slice(0, state.visible);
      count.textContent = records.length > visibleRecords.length
        ? "Showing " + visibleRecords.length + " of " + records.length
        : records.length + " locations";
      cards.innerHTML = visibleRecords.length ? visibleRecords.map((record) => storeCard(record, { selectedId: state.selectedId })).join("") : '<div class="fl-stockists__empty">No matching stockists found in this province.</div>';
      loadMore.hidden = visibleRecords.length >= records.length;
      if (!loadMore.hidden) loadMore.textContent = "Show " + Math.min(PAGE_SIZE, records.length - visibleRecords.length) + " more locations";
      updateMarkers(records);
    }

    cards.addEventListener("click", (event) => {
      const card = event.target.closest("[data-record-id]");
      if (!card || event.target.closest("a")) return;
      state.selectedId = card.dataset.recordId || "";
      const marker = state.markers.get(state.selectedId);
      if (marker && state.map) {
        state.map.panTo(marker.getPosition());
        state.map.setZoom(Math.max(state.map.getZoom() || 9, 13));
        window.google.maps.event.trigger(marker, "click");
      }
      renderCards();
    });

    input.addEventListener("input", () => {
      state.query = clean(input.value);
      state.selectedId = "";
      state.visible = PAGE_SIZE;
      renderCards();
    });

    root.querySelectorAll("[data-type]").forEach((button) => {
      button.addEventListener("click", () => {
        state.type = button.dataset.type || "all";
        state.selectedId = "";
        state.visible = PAGE_SIZE;
        root.querySelectorAll("[data-type]").forEach((entry) => entry.classList.toggle("is-active", entry === button));
        renderCards();
      });
    });

    loadMore.addEventListener("click", () => {
      state.visible += PAGE_SIZE;
      renderCards();
    });

    root.querySelectorAll("[data-view]").forEach((button) => {
      button.addEventListener("click", () => {
        const view = button.dataset.view === "map" ? "map" : "list";
        layout.dataset.mobileView = view;
        root.querySelectorAll("[data-view]").forEach((entry) => {
          const active = entry === button;
          entry.classList.toggle("is-active", active);
          entry.setAttribute("aria-pressed", String(active));
        });
        if (view === "map" && state.map && window.google?.maps) {
          window.google.maps.event.trigger(state.map, "resize");
          updateMarkers(filtered());
        }
      });
    });

    renderCards();
    loadMaps(googleKey).then(() => {
      state.map = new window.google.maps.Map(mapEl, {
        center: { lat: -30.5595, lng: 22.9375 },
        zoom: 6,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      });
      state.info = new window.google.maps.InfoWindow();
      updateMarkers(filtered());
    }).catch((error) => {
      console.error("Flippen Lekka stockist map failed", error);
      mapEl.innerHTML = '<div class="fl-stockists__map-fallback"><h3>Map unavailable</h3><p>The directory is still available. Use the Open in Google Maps link on any stockist card for directions.</p></div>';
    });
  }

  async function initRoot(root) {
    try {
      const data = await loadData(root.dataset.dataUrl);
      const mode = root.dataset.mode || "hub";
      const provinceName = clean(root.dataset.province) || provinceFromHandle(root.dataset.pageHandle);
      if (mode === "province" || provinceName) {
        renderProvince(root, data, provinceName || "Gauteng", root.dataset.googleKey || "");
      } else {
        renderHub(root, data);
      }
    } catch (error) {
      root.innerHTML = '<div class="fl-stockists__wrap"><div class="fl-stockists__empty"><h1>Stockists could not load</h1><p>' + escapeHtml(error.message) + '</p><p><a href="/pages/contact">Contact Flippen Lekka</a></p></div></div>';
      console.error(error);
    }
  }

  function init() {
    document.querySelectorAll("[data-fl-stockists]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
